import { types } from 'mobx-state-tree';

import FieldType, { FieldTypeType } from '../types/FieldType';
import { FormFieldIdType } from '../types/FormFieldId';
import { FormFieldTypeType } from '../types/FormFieldType';
import SelectOption from './form/SelectOption';

/** Single references so MST can reconcile array assignments (mobx-state-tree #1496). */
const formFieldStringArray = types.array(types.string);
const formFieldNumberArray = types.array(types.number);

/**
 * Dispatcher resolves ambiguous `[]` between string[] and number[] (mobx-state-tree #1907).
 */
const formFieldValueUnion = types.union(
  {
    dispatcher(snapshot) {
      if (Array.isArray(snapshot)) {
        if (snapshot.length === 0) {
          return formFieldStringArray;
        }
        return typeof snapshot[0] === 'number'
          ? formFieldNumberArray
          : formFieldStringArray;
      }
      if (typeof snapshot === 'boolean') {
        return types.boolean;
      }
      if (typeof snapshot === 'number') {
        return types.number;
      }
      return types.string;
    },
  },
  types.string,
  types.number,
  formFieldStringArray,
  formFieldNumberArray,
  types.boolean
);

/*
 * A FormField
 * Unified with CustomFormField
 */
const FormField = types
  .model('FormField', {
    // The previous value of the field for blocking unnecessary updates
    previousValue: types.maybeNull(formFieldValueUnion),
    // The value of the field
    value: types.maybeNull(formFieldValueUnion),
    type: types.optional(
      types.union(FormFieldTypeType, FieldTypeType),
      FieldType.NORMAL
    ),
    // If true, the "valid" and "error" properties will change every time the
    // value changes.
    shouldValidate: types.maybeNull(types.optional(types.boolean, false)),
    // External index to map the field event to certain source data.
    externalIndex: types.maybeNull(types.number),
    id: types.maybeNull(
      types.union(FormFieldIdType, types.number, types.string)
    ),
    name: types.maybeNull(types.string),
    min_length: types.maybeNull(types.number),
    max_length: types.maybeNull(types.number),
    options: types.maybeNull(types.optional(types.array(SelectOption), [])),
    required: types.maybeNull(types.optional(types.boolean, false)),
  })
  .extend((self) => {
    let validators, isActive;
    return {
      views: {
        get errors() {
          const errors = [];
          if (self.shouldValidate && validators) {
            validators.forEach((validator) => {
              const error = validator(self.value);
              if (error) {
                errors.push(error);
              }
            });
          }
          return errors;
        },
        get valid() {
          return self.shouldValidate ? self.errors.length === 0 : null;
        },
        get active() {
          return self.isActive();
        },
        isActive() {
          return isActive ? isActive() : true;
        },
      },
      actions: {
        setValue(value) {
          self.previousValue = self.value;
          self.value = value;
        },
        updatePreviousValue() {
          self.previousValue = self.value;
        },
        setShouldValidate(value) {
          self.shouldValidate = value;
        },
        setActive(value) {
          self.active = value;
        },
        /**
         * @param {function[]} newValidators
         */
        setValidators(newValidators) {
          validators = newValidators;
        },
        hasValidators() {
          return Array.isArray(validators) && validators.length > 0;
        },
        /**
         * @param {function} newIsActive
         */
        setIsActive(newIsActive) {
          isActive = newIsActive;
        },
      },
    };
  });
export default FormField;

export function createFormField(field = {}, validators, isActive) {
  const fieldModel = FormField.create(field);
  if (validators) {
    fieldModel.setValidators(validators);
  }
  if (isActive) {
    fieldModel.setIsActive(isActive);
  }
  return fieldModel;
}
